import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Link2, Plus, Ticket, Trash2, UserPlus } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    AccessCode,
    AccessCodeUser,
    activationLink,
    createAccessCode,
    createAccountWithCode,
    deleteAccessCode,
    getAccessCodes,
    getAccessCodeUsers,
    signupLink,
    updateAccessCode,
} from "interfaces/accessCodes";

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputClass = "w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400";
const labelClass = "text-xs text-gray-600 mb-1";

function formatDate(d?: string) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // navigator.clipboard is unavailable outside secure contexts (plain http)
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(el);
        return ok;
    }
}

function CopyButton({ text, label, className }: { text: string; label: string; className?: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            onClick={async () => {
                if (await copyText(text)) {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                }
            }}
            className={className ?? "text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white hover:bg-[#333] shrink-0"}
        >
            {copied ? "Copié !" : label}
        </button>
    );
}

function AccessBadge({ code }: { code: Pick<AccessCode, "premium" | "trialDays"> }) {
    return code.premium ? (
        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 border border-green-200">Premium</span>
    ) : (
        <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">Essai {code.trialDays} j</span>
    );
}

/** Premium or trial + its length: the one choice that decides what an account made with the code gets. */
function AccessChoice({ premium, trialDays, onChange }: { premium: boolean; trialDays: number; onChange: (v: { premium: boolean; trialDays: number }) => void }) {
    return (
        <div className="flex items-center gap-2">
            {[true, false].map((p) => (
                <button
                    key={String(p)}
                    type="button"
                    onClick={() => onChange({ premium: p, trialDays })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${premium === p ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                    {p ? "Premium (accès complet)" : "Essai"}
                </button>
            ))}
            {!premium && (
                <span className="flex items-center gap-1 text-xs text-gray-600">
                    <input
                        type="number"
                        min={1}
                        max={365}
                        value={trialDays}
                        onChange={(e) => onChange({ premium, trialDays: Number(e.target.value) })}
                        className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                    jours
                </span>
            )}
        </div>
    );
}

function CreateCodePanel({ onCreated }: { onCreated: (c: AccessCode) => void }) {
    const [code, setCode] = useState("");
    const [label, setLabel] = useState("");
    const [access, setAccess] = useState({ premium: true, trialDays: 7 });
    const [maxUses, setMaxUses] = useState(0);
    const [expiresAt, setExpiresAt] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async () => {
        setSaving(true);
        setError(null);
        const res = await createAccessCode({ code: code.trim() || undefined, label: label.trim(), ...access, maxUses, expiresAt: expiresAt || null });
        setSaving(false);
        if (res.error || !res.code) return setError(res.error || "Impossible de créer le code");
        setCode("");
        setLabel("");
        setMaxUses(0);
        setExpiresAt("");
        onCreated(res.code);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
                <Plus className="w-4 h-4 text-gray-500" />
                <p className="text-sm font-semibold text-gray-900">Nouveau code</p>
            </div>
            <p className="text-xs text-gray-500 mb-3">Le client le saisit à l'inscription, ou reçoit le lien d'inscription qui le pré-remplit.</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                    <p className={labelClass}>Code</p>
                    <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Généré si vide" className={`${inputClass} font-mono uppercase`} />
                </div>
                <div>
                    <p className={labelClass}>Client / note</p>
                    <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex. Groupe Bertrand" className={inputClass} />
                </div>
            </div>
            <div className="mb-3">
                <p className={labelClass}>Accès donné</p>
                <AccessChoice {...access} onChange={setAccess} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                    <p className={labelClass}>Utilisations max (0 = illimité)</p>
                    <input type="number" min={0} value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} className={inputClass} />
                </div>
                <div>
                    <p className={labelClass}>Expire le (facultatif)</p>
                    <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={inputClass} />
                </div>
            </div>
            {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
            <button onClick={create} disabled={saving} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white hover:bg-[#333] disabled:opacity-50">
                {saving ? "Création…" : "Créer le code"}
            </button>
        </div>
    );
}

/** Onboard a client by hand: create their account with a code, or apply the code to their existing account. */
function OnboardPanel({ codes, codeId, setCodeId, onDone }: { codes: AccessCode[]; codeId: string; setCodeId: (id: string) => void; onDone: () => void }) {
    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");
    const [company, setCompany] = useState("");
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [exists, setExists] = useState<AccessCodeUser | null>(null);
    const [result, setResult] = useState<{ user: AccessCodeUser; created: boolean; link?: string; code: AccessCode } | null>(null);

    const code = codes.find((c) => c._id === codeId);
    const usable = codes.filter((c) => c.active);
    const normalizedEmail = email.trim().toLowerCase();
    const canSubmit = !!code && EMAIL_RX.test(normalizedEmail) && !!fullName.trim() && !saving;

    useEffect(() => setExists(null), [normalizedEmail, codeId]);

    const submit = async (applyToExisting = false) => {
        if (!code) return;
        setSaving(true);
        setError(null);
        const res = await createAccountWithCode(code._id, { email: normalizedEmail, fullName: fullName.trim(), company: company.trim(), password: password || undefined, applyToExisting });
        setSaving(false);
        if (res.exists) return setExists(res.exists);
        if (res.error || !res.user) return setError(res.error || "Impossible de créer le compte");
        setResult({ user: res.user, created: !!res.created, link: res.activationToken ? activationLink(res.activationToken) : undefined, code });
        setExists(null);
        setEmail("");
        setFullName("");
        setCompany("");
        setPassword("");
        onDone();
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
                <UserPlus className="w-4 h-4 text-gray-500" />
                <p className="text-sm font-semibold text-gray-900">Onboarder un client</p>
            </div>
            <p className="text-xs text-gray-500 mb-3">Crée le compte avec le code choisi : il reçoit l'accès du code (premium ou essai) immédiatement.</p>

            {result && (
                <div className="mb-3 border border-green-200 bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-800 mb-2">
                        {result.created ? "Compte créé" : "Code appliqué au compte"} <strong>{result.user.email}</strong>
                        {result.user.isSub ? " — premium." : result.user.trialEndsAt ? ` — essai jusqu'au ${formatDate(result.user.trialEndsAt)}.` : "."}
                        {result.link && " Envoyez-lui ce lien pour qu'il choisisse son mot de passe :"}
                    </p>
                    {result.link && (
                        <div className="flex gap-2">
                            <input readOnly value={result.link} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 text-xs font-mono px-3 py-1.5 border border-green-200 rounded-lg bg-white" />
                            <CopyButton text={result.link} label="Copier" />
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="col-span-2">
                    <p className={labelClass}>Code</p>
                    <select value={codeId} onChange={(e) => setCodeId(e.target.value)} className={inputClass}>
                        <option value="">Choisir un code…</option>
                        {usable.map((c) => (
                            <option key={c._id} value={c._id}>
                                {c.code}
                                {c.label ? ` · ${c.label}` : ""} · {c.premium ? "Premium" : `Essai ${c.trialDays} j`}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <p className={labelClass}>Email</p>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom@entreprise.fr" className={inputClass} />
                </div>
                <div>
                    <p className={labelClass}>Nom complet</p>
                    <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Prénom Nom" className={inputClass} />
                </div>
                <div>
                    <p className={labelClass}>Entreprise (facultatif)</p>
                    <input value={company} onChange={(e) => setCompany(e.target.value)} className={inputClass} />
                </div>
                <div>
                    <p className={labelClass}>Mot de passe (facultatif)</p>
                    <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Vide = lien d'activation" className={inputClass} />
                </div>
            </div>

            {exists && (
                <div className="mb-3 border border-gray-200 bg-[#F9F6F3] rounded-lg p-3">
                    <p className="text-xs text-gray-700 mb-2">
                        Un compte existe déjà pour <strong>{exists.email}</strong> ({exists.isSub ? "premium" : exists.trialEndsAt ? `essai jusqu'au ${formatDate(exists.trialEndsAt)}` : "sans abonnement"}). Appliquer le code{" "}
                        <span className="font-mono">{code?.code}</span> à ce compte ?
                    </p>
                    <div className="flex gap-2">
                        <button onClick={() => submit(true)} disabled={saving} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white hover:bg-[#333] disabled:opacity-50">
                            Appliquer au compte existant
                        </button>
                        <button onClick={() => setExists(null)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
                            Annuler
                        </button>
                    </div>
                </div>
            )}

            {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
            {!exists && (
                <button onClick={() => submit()} disabled={!canSubmit} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white hover:bg-[#333] disabled:opacity-50">
                    {saving ? "Création…" : "Créer le compte"}
                </button>
            )}
        </div>
    );
}

function CodeDetail({ code, onSaved }: { code: AccessCode; onSaved: (c: AccessCode) => void }) {
    const [users, setUsers] = useState<AccessCodeUser[] | null>(null);
    const [label, setLabel] = useState(code.label ?? "");
    const [access, setAccess] = useState({ premium: code.premium, trialDays: code.trialDays });
    const [maxUses, setMaxUses] = useState(code.maxUses);
    const [expiresAt, setExpiresAt] = useState(code.expiresAt ? code.expiresAt.slice(0, 10) : "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getAccessCodeUsers(code._id).then(setUsers).catch(() => setUsers([]));
    }, [code._id]);

    const save = async () => {
        setSaving(true);
        setError(null);
        const res = await updateAccessCode(code._id, { label: label.trim(), ...access, maxUses, expiresAt: expiresAt || null });
        setSaving(false);
        if (res.error || !res.code) return setError(res.error || "Enregistrement impossible");
        onSaved({ ...code, ...res.code });
    };

    return (
        <div className="px-4 py-4 bg-[#F9F6F3] border-t border-gray-100 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-600 mb-3">Paramètres</p>
                <div className="mb-3">
                    <p className={labelClass}>Client / note</p>
                    <input value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />
                </div>
                <div className="mb-3">
                    <p className={labelClass}>Accès donné aux prochains comptes</p>
                    <AccessChoice {...access} onChange={setAccess} />
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <p className={labelClass}>Utilisations max (0 = illimité)</p>
                        <input type="number" min={0} value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} className={inputClass} />
                    </div>
                    <div>
                        <p className={labelClass}>Expire le</p>
                        <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={inputClass} />
                    </div>
                </div>
                <p className="text-[11px] text-gray-500 mb-2">Les comptes déjà créés gardent l'accès qu'ils ont reçu.</p>
                {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
                <button onClick={save} disabled={saving} className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white hover:bg-[#333] disabled:opacity-50">
                    {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg">
                <p className="text-xs font-medium text-gray-600 px-3 py-2 border-b border-gray-100">Comptes créés avec ce code</p>
                {users === null ? (
                    <p className="text-xs text-gray-400 px-3 py-3">Chargement…</p>
                ) : users.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-3">Aucun compte.</p>
                ) : (
                    <ul className="max-h-72 overflow-y-auto">
                        {users.map((u) => (
                            <li key={u._id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs border-b border-gray-50 last:border-0">
                                <span className="min-w-0">
                                    <Link to={`/bo/activity?user=${u._id}`} className="block text-gray-900 truncate hover:underline">
                                        {u.email}
                                    </Link>
                                    {(u.name || u.company) && <span className="block text-gray-500 truncate">{[u.name, u.company].filter(Boolean).join(" · ")}</span>}
                                </span>
                                <span className="shrink-0 text-right">
                                    {u.isSub ? <span className="text-green-700">Premium</span> : <span className="text-gray-500">Essai → {formatDate(u.trialEndsAt)}</span>}
                                    <span className="block text-gray-400">créé le {formatDate(u.createdAt)}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function AccessCodes() {
    const [codes, setCodes] = useState<AccessCode[]>([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [onboardCodeId, setOnboardCodeId] = useState("");
    const onboardRef = useRef<HTMLDivElement>(null);

    const fetchCodes = () =>
        getAccessCodes()
            .then(setCodes)
            .catch(() => setCodes([]))
            .finally(() => setLoading(false));

    useEffect(() => {
        fetchCodes();
    }, []);

    const replace = (c: AccessCode) => setCodes((prev) => prev.map((x) => (x._id === c._id ? { ...x, ...c } : x)));

    const toggleActive = async (c: AccessCode) => {
        const res = await updateAccessCode(c._id, { active: !c.active });
        if (res.error || !res.code) return window.alert(res.error);
        replace(res.code as AccessCode);
    };

    const remove = async (c: AccessCode) => {
        if (!window.confirm(`Supprimer le code ${c.code} ?\nIl ne permettra plus de créer de compte. Les ${c.accounts} compte(s) déjà créé(s) gardent leur accès.`)) return;
        const res = await deleteAccessCode(c._id);
        if (res.error) return window.alert(res.error);
        setCodes((prev) => prev.filter((x) => x._id !== c._id));
    };

    const onboardWith = (c: AccessCode) => {
        setOnboardCodeId(c._id);
        onboardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const activeCodes = codes.filter((c) => c.active).length;
    const accounts = codes.reduce((s, c) => s + c.accounts, 0);
    const premiumAccounts = codes.reduce((s, c) => s + c.premiumAccounts, 0);

    const usesLabel = (c: AccessCode) => `${c.usageCount}${c.maxUses > 0 ? ` / ${c.maxUses}` : ""}`;
    const isExpired = (c: AccessCode) => !!c.expiresAt && new Date(c.expiresAt) < new Date();
    const isExhausted = (c: AccessCode) => c.maxUses > 0 && c.usageCount >= c.maxUses;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">Codes d'accès</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Les codes qui ouvrent un compte, et l'onboarding des clients</p>
                </div>

                <BONavigation />

                <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Codes actifs</p>
                        <p className="text-2xl font-semibold text-gray-900">{activeCodes}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Comptes créés avec un code</p>
                        <p className="text-2xl font-semibold text-gray-900">{accounts}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Dont premium</p>
                        <p className="text-2xl font-semibold text-gray-900">{premiumAccounts}</p>
                    </div>
                </div>

                <div ref={onboardRef} className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 scroll-mt-4">
                    <CreateCodePanel
                        onCreated={(c) => {
                            setCodes((prev) => [c, ...prev]);
                            setOnboardCodeId(c._id);
                        }}
                    />
                    <OnboardPanel codes={codes} codeId={onboardCodeId} setCodeId={setOnboardCodeId} onDone={fetchCodes} />
                </div>

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-8">
                    {loading && codes.length === 0 ? (
                        <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Chargement…</div>
                    ) : codes.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-2">
                            <Ticket className="w-5 h-5" />
                            Aucun code
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="w-8" />
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Code</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Client / note</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Accès</th>
                                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Utilisations</th>
                                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Comptes</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Expiration</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Statut</th>
                                    <th className="w-48" />
                                </tr>
                            </thead>
                            <tbody>
                                {codes.map((c) => {
                                    const open = expanded === c._id;
                                    const unusable = !c.active ? "Désactivé" : isExpired(c) ? "Expiré" : isExhausted(c) ? "Épuisé" : null;
                                    return [
                                        <tr key={c._id} className={`border-b border-gray-50 hover:bg-[#F9F6F3] ${unusable ? "text-gray-400" : ""}`}>
                                            <td className="pl-3">
                                                <button onClick={() => setExpanded(open ? null : c._id)} className="text-gray-400 hover:text-gray-700">
                                                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-xs font-mono font-medium text-gray-900">{c.code}</td>
                                            <td className="px-4 py-3 text-xs text-gray-700">{c.label || <span className="text-gray-400">—</span>}</td>
                                            <td className="px-4 py-3">
                                                <AccessBadge code={c} />
                                            </td>
                                            <td className="px-4 py-3 text-xs text-right text-gray-900">{usesLabel(c)}</td>
                                            <td className="px-4 py-3 text-xs text-right text-gray-900">
                                                {c.accounts}
                                                {c.premiumAccounts > 0 && <span className="text-gray-500"> ({c.premiumAccounts} premium)</span>}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-500">{formatDate(c.expiresAt)}</td>
                                            <td className="px-4 py-3">
                                                <button
                                                    onClick={() => toggleActive(c)}
                                                    title={c.active ? "Désactiver" : "Réactiver"}
                                                    className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                                                        unusable ? "bg-red-50 text-red-600 border-red-200" : "bg-green-50 text-green-700 border-green-200"
                                                    }`}
                                                >
                                                    {unusable ?? "Actif"}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <CopyButton
                                                        text={signupLink(c.code)}
                                                        label="Lien d'inscription"
                                                        className="inline-flex items-center text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                                                    />
                                                    {c.active && (
                                                        <button onClick={() => onboardWith(c)} title="Créer un compte avec ce code" className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100">
                                                            <UserPlus className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <button onClick={() => remove(c)} title="Supprimer" className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>,
                                        open && (
                                            <tr key={`${c._id}-detail`}>
                                                <td colSpan={9} className="p-0">
                                                    <CodeDetail code={c} onSaved={replace} />
                                                </td>
                                            </tr>
                                        ),
                                    ];
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <p className="flex items-center gap-1.5 text-xs text-gray-500 mb-8">
                    <Link2 className="w-3.5 h-3.5" />
                    Le lien d'inscription pré-remplit le code : <span className="font-mono">/auth/register?trial_acces=CODE</span>. Un compte Google saisit le code juste après sa première connexion.
                </p>
            </div>
        </div>
    );
}
